import { useState, useCallback, useRef, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { buildSubscriptionUrl } from '../../config/env';

export const useSubscriptionManager = () => {
  const [state, setState] = useState({ hasSubscription: false, hasMaxSubscription: false, hasBasicSubscription: false, subscriptionSources: [], isCheckingSubscription: true, subscriptionStatus: 'unknown' });
  const [isActivating, setIsActivating] = useState(() => new URLSearchParams(window.location.search).get('billing') === 'success');
  const generation = useRef(0);
  const account = useRef(null);
  const pending = useRef(null);
  const lastVerified = useRef(null);

  const checkSubscription = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || user.uid !== account.current) return;
    if (pending.current) return pending.current.promise;
    const epoch = generation.current;
    const controller = new AbortController();
    const operation = (async () => {
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const token = await user.getIdToken();
        if (generation.current !== epoch) return;
        const response = await fetch(buildSubscriptionUrl('/api/v1/me/entitlements'), {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (!response.ok) throw new Error('verification_unavailable');
        const result = await response.json();
        if (generation.current !== epoch || auth.currentUser?.uid !== user.uid) return;
        if (result.uid !== user.uid || result.version !== 1 || !['active', 'inactive'].includes(result.chat?.status)) throw new Error('verification_unavailable');
        const active = result.chat.status === 'active';
        let expires = active ? Math.min(Date.parse(result.chat.validUntil), Date.now() + 300000) : Date.now() + 60000;
        if (!Number.isFinite(expires) || expires <= Date.now()) throw new Error('verification_unavailable');
        const hasMaxSubscription = active && result.work?.status === 'active' && Date.parse(result.work.validUntil) > Date.now();
        if (hasMaxSubscription) expires = Math.min(expires, Date.parse(result.work.validUntil));
        const verified = { hasSubscription: active, hasMaxSubscription, hasBasicSubscription: result.basic?.status === 'active', subscriptionSources: result.chat.sources || [], isCheckingSubscription: false, subscriptionStatus: result.chat.status };
        lastVerified.current = { state: verified, expires };
        setState(verified);
        if (active) setIsActivating(false);
      } catch {
        if (generation.current !== epoch) return;
        const cached = lastVerified.current;
        setState({ ...(cached?.expires > Date.now() ? cached.state : { hasSubscription: false, hasMaxSubscription: false, hasBasicSubscription: false, subscriptionSources: [] }), isCheckingSubscription: false, subscriptionStatus: 'unknown' });
      } finally {
        clearTimeout(timeout);
        if (generation.current === epoch) pending.current = null;
      }
    })();
    pending.current = { controller, promise: operation };
    return operation;
  }, []);

  useEffect(() => {
    let activationAttempts = 0;
    const activating = new URLSearchParams(window.location.search).get('billing') === 'success';
    const unsubscribe = onAuthStateChanged(auth, user => {
      generation.current += 1;
      pending.current?.controller.abort(); pending.current = null; lastVerified.current = null;
      account.current = user?.uid || null;
      setState({ hasSubscription: false, hasMaxSubscription: false, hasBasicSubscription: false, subscriptionSources: [], isCheckingSubscription: Boolean(user), subscriptionStatus: user ? 'unknown' : 'inactive' });
      if (user) checkSubscription();
    });
    const foreground = () => { if (document.visibilityState === 'visible') checkSubscription(); };
    window.addEventListener('online', checkSubscription);
    document.addEventListener('visibilitychange', foreground);
    const timer = setInterval(() => {
      if (activating && activationAttempts++ < 12) checkSubscription();
      else if (document.visibilityState === 'visible' && (!lastVerified.current || lastVerified.current.expires <= Date.now())) checkSubscription();
    }, 10000);
    return () => {
      unsubscribe(); generation.current += 1; pending.current?.controller.abort(); pending.current = null;
      clearInterval(timer); window.removeEventListener('online', checkSubscription); document.removeEventListener('visibilitychange', foreground);
    };
  }, [checkSubscription]);
  return { ...state, isActivating, checkSubscription, initializeSubscriptionCheck: checkSubscription };
};
